import useSWR from 'swr';
import { useEffect } from 'react';
import { EngineService } from '@/services/engine';
import { useSourcesStore, useQueryResultsStore } from '@/stores';
import type { PipContentMode } from '@/stores/pip-player.store';
import {
    getEventCacheKey,
    getSourceCacheKey,
    getReportEventCacheKey,
} from '@/constants/cache-keys';
import { reportEventDetailToEvent } from '@/features/ReportEventView/utils/reportEventDetailToEvent';
import { reportEventVideoStartSeconds } from '@/features/ReportEventView/utils/reportEventVideoStartSeconds';
import type { Event } from '@/types/event';
import type { FullSource } from '@/types/source';
import { RangeOrigin, type EventPlaybackRange } from '@/types/video-player';
import {
    deriveEventPlaybackRange,
    getTimeDomain,
} from '@/utils/event-playback-range';
import { ReportEventType, type ReportEventDetail } from '@/types/report';

interface PlaybackRange {
    startTime: number;
    endTime: number;
}

interface EventPlaybackData {
    event: Event | undefined;
    source: FullSource | undefined;
    confidence: number | undefined;
    focuses: number[][] | undefined;
    startTime: number | undefined;
    eventRange: EventPlaybackRange | undefined;
    isLoading: boolean;
    error: unknown;
}

interface ReportEventPlayback {
    event: Event & { confidence?: number };
    startTime: number;
    playbackRange?: PlaybackRange;
}

function buildReportEventPlayback(
    detail: ReportEventDetail,
): ReportEventPlayback {
    const event = reportEventDetailToEvent(detail);
    const startTime = reportEventVideoStartSeconds(detail);

    if (detail.type === ReportEventType.CUSTOM) {
        return {
            event: { ...event, frames: [] },
            startTime,
            playbackRange: {
                startTime,
                endTime: detail.videoEndSeconds ?? 0,
            },
        };
    }

    return { event, startTime };
}

export function useEventPlaybackData(
    eventId: string | null,
    mode: PipContentMode,
): EventPlaybackData {
    const isReportEvent = mode === 'report-event';
    const queryEvents = useQueryResultsStore((s) => s.events);
    const getSourceById = useSourcesStore((s) => s.getSourceById);
    const addSource = useSourcesStore((s) => s.addSource);

    const scoredEvent = queryEvents.find((e) => e.event.id === eventId);
    const cachedEvent = scoredEvent?.event;

    const {
        data: fetchedEvent,
        error: eventError,
        isLoading: eventLoading,
    } = useSWR<Event>(
        !isReportEvent && eventId && !cachedEvent
            ? getEventCacheKey(eventId)
            : null,
        () => EngineService.getEvent(eventId!, scoredEvent?.event.type),
    );

    const {
        data: reportEventDetail,
        error: reportEventError,
        isLoading: reportEventLoading,
    } = useSWR<ReportEventDetail>(
        isReportEvent && eventId ? getReportEventCacheKey(eventId) : null,
        () => EngineService.getReportEvent(eventId!),
    );

    const reportPlayback = reportEventDetail
        ? buildReportEventPlayback(reportEventDetail)
        : undefined;

    const event = isReportEvent
        ? reportPlayback?.event
        : (fetchedEvent ?? cachedEvent);
    const sourceId = event?.sourceId;
    const cachedSource = sourceId ? getSourceById(sourceId) : undefined;

    const {
        data: fetchedSource,
        error: sourceError,
        isLoading: sourceLoading,
    } = useSWR<FullSource>(
        sourceId && !cachedSource ? getSourceCacheKey(sourceId) : null,
        () => EngineService.getSource(sourceId!),
    );

    useEffect(() => {
        if (fetchedSource) {
            addSource(fetchedSource);
        }
    }, [fetchedSource, addSource]);

    const source = cachedSource ?? fetchedSource;
    const focuses = isReportEvent
        ? []
        : (scoredEvent?.focuses ?? event?.focuses);

    const reportRange = reportPlayback?.playbackRange;
    const eventRange =
        !event || !source
            ? undefined
            : reportRange
              ? {
                    domain: getTimeDomain(source.type),
                    origin: RangeOrigin.SELECTION,
                    start: reportRange.startTime,
                    end: reportRange.endTime,
                    playbackStart: reportRange.startTime,
                }
              : (deriveEventPlaybackRange({
                    event,
                    sourceType: source.type,
                    focuses,
                }) ?? undefined);

    return {
        event,
        source,
        confidence: isReportEvent
            ? reportPlayback?.event.confidence
            : scoredEvent?.confidence,
        focuses,
        startTime: reportPlayback?.startTime,
        eventRange,
        isLoading: eventLoading || reportEventLoading || sourceLoading,
        error: eventError ?? reportEventError ?? sourceError,
    };
}
